import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

export const SEARCH_HIGHLIGHT_NAME = 'documentation-search';
const MAX_HIGHLIGHTS = 1000;

type HighlightApi = {
    readonly registry: {
        set(name: string, highlight: unknown): void;
        delete(name: string): void;
    };
    readonly Highlight: new (...ranges: Range[]) => unknown;
};

// CSS Custom Highlight API: highlights text without touching the DOM React owns.
// Not in the TypeScript 4.8 DOM typings, and absent in older browsers (search still works there).
const getHighlightApi = (): HighlightApi | null => {
    const registry = (globalThis.CSS as unknown as { highlights?: HighlightApi['registry'] } | undefined)?.highlights;
    const Highlight = (globalThis as unknown as { Highlight?: HighlightApi['Highlight'] }).Highlight;

    return registry && Highlight ? { registry, Highlight } : null;
};

export const getSearchTerms = (query: string) =>
    query.trim().toLowerCase().split(/\s+/).filter(Boolean);

export const sectionMatches = (text: string, terms: readonly string[]) => {
    const normalizedText = text.toLowerCase();

    return terms.every(term => normalizedText.includes(term));
};

const findTermRanges = (sections: readonly HTMLElement[], terms: readonly string[]) => {
    const ranges: Range[] = [];

    for (const section of sections) {
        const walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT);

        for (let node = walker.nextNode(); node && ranges.length < MAX_HIGHLIGHTS; node = walker.nextNode()) {
            const text = node.textContent?.toLowerCase() ?? '';

            for (const term of terms) {
                for (let index = text.indexOf(term); index !== -1; index = text.indexOf(term, index + term.length)) {
                    const range = document.createRange();
                    range.setStart(node, index);
                    range.setEnd(node, index + term.length);
                    ranges.push(range);
                }
            }
        }
    }

    return ranges;
};

/**
 * Full-text search over the rendered documentation. Every element marked with
 * `data-doc-section` is matched against its complete text content, so prose, tables and
 * code samples are all searchable without keeping a separate index in sync.
 *
 * `contentKey` must change whenever asynchronously loaded content changes the page text.
 */
const useDocumentationSearch = (
    rootRef: RefObject<HTMLElement>,
    query: string,
    contentKey: unknown
) => {
    const [matches, setMatches] = useState<ReadonlySet<string> | null>(null);
    const firstMatchRef = useRef<Range | null>(null);

    useEffect(() => {
        const root = rootRef.current;
        const terms = getSearchTerms(query);

        if (!root || !terms.length) {
            setMatches(null);
            return;
        }

        const sections = Array.from(root.querySelectorAll<HTMLElement>('[data-doc-section]'));
        setMatches(new Set(
            sections
                .filter(section => sectionMatches(section.textContent ?? '', terms))
                .map(section => section.id)
        ));
    }, [rootRef, query, contentKey]);

    // Runs after the matching sections are visible and collapsed code blocks have expanded.
    useEffect(() => {
        const root = rootRef.current;

        if (!root) {
            return;
        }

        const visibleSections = matches
            ? Array.from(root.querySelectorAll<HTMLElement>('[data-doc-section]')).filter(section => matches.has(section.id))
            : [];
        const ranges = findTermRanges(visibleSections, matches ? getSearchTerms(query) : []);
        const api = getHighlightApi();

        firstMatchRef.current = ranges[0] ?? null;
        api?.registry.delete(SEARCH_HIGHLIGHT_NAME);
        if (api && ranges.length) {
            api.registry.set(SEARCH_HIGHLIGHT_NAME, new api.Highlight(...ranges));
        }
    }, [rootRef, matches, query]);

    useEffect(() => () => getHighlightApi()?.registry.delete(SEARCH_HIGHLIGHT_NAME), []);

    const scrollToFirstMatch = useCallback(() => {
        firstMatchRef.current?.startContainer.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, []);

    return { matches, scrollToFirstMatch };
};

export default useDocumentationSearch;
